import { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, onSnapshot, setDoc, collection, query, where, getDocs, writeBatch } from 'firebase/firestore';
import { auth, db, storage } from '../firebaseConfig'; // Importa instâncias já inicializadas

/**
 * Hook customizado para gerenciar a autenticação do Firebase e os dados do usuário.
 * Utiliza as instâncias de serviços Firebase já inicializadas em 'firebaseConfig.js'.
 * @returns {{
 * auth: import('firebase/auth').Auth | null,
 * user: import('firebase/auth').User | null,
 * userData: object | null,
 * authLoading: boolean,
 * db: import('firestore').Firestore | null,
 * storage: import('storage').FirebaseStorage | null
 * }} Objeto contendo o estado da autenticação e os dados do usuário.
 */
export const useAuth = () => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [authLoading, setAuthLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
            if (firebaseUser) {
                const userDocRef = doc(db, 'users', firebaseUser.uid);
                
                const unsubscribeDoc = onSnapshot(userDocRef, async (userDocSnap) => {
                    if (userDocSnap.exists()) {
                        const newUserData = { ...userDocSnap.data(), uid: firebaseUser.uid };
                        setUser({ ...firebaseUser, ...newUserData });
                        setUserData(newUserData);
                    } else {
                        const isPasswordProvider = firebaseUser.providerData.some(
                            (provider) => provider.providerId === 'password'
                        );

                        if (!isPasswordProvider) {
                            const newUser = {
                                email: firebaseUser.email,
                                displayName: firebaseUser.displayName,
                                role: 'pending',
                                photoURL: firebaseUser.photoURL,
                                createdAt: new Date().toISOString(),
                                theme: 'light',
                            };
                            await setDoc(userDocRef, newUser);

                            const usersQuery = query(collection(db, 'users'), where('role', '==', 'administrador'));
                            const adminSnapshot = await getDocs(usersQuery);
                            const batch = writeBatch(db);
                            adminSnapshot.forEach(adminDoc => {
                                const notificationRef = doc(collection(db, 'notifications'));
                                batch.set(notificationRef, {
                                    userId: adminDoc.id,
                                    message: `Novo usuário (${firebaseUser.email}) aguardando aprovação.`,
                                    type: 'newUser',
                                    relatedId: firebaseUser.uid,
                                    createdAt: new Date().toISOString(),
                                    read: false,
                                });
                            });
                            await batch.commit();

                            setUser({ ...firebaseUser, ...newUser });
                            setUserData({ ...newUser, uid: firebaseUser.uid });
                        }
                    }
                    setAuthLoading(false);
                });

                return () => unsubscribeDoc();
            } else {
                setUser(null);
                setUserData(null);
                setAuthLoading(false);
            }
        });

        return () => unsubscribe();
    }, []); // As dependências auth e db são estáveis e não precisam ser listadas

    return { auth, user, userData, authLoading, db, storage };
};
